import { useState } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';

interface Requirement {
  label: string;
  test: (v: string) => boolean;
}

const REQUIREMENTS: Requirement[] = [
  { label: 'Mínimo 8 caracteres',      test: v => v.length >= 8 },
  { label: 'Pelo menos uma letra',      test: v => /[a-zA-Z]/.test(v) },
  { label: 'Pelo menos um número',      test: v => /\d/.test(v) },
];

interface PasswordFieldProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (v: string) => void;
  showChecklist?: boolean;
  autoComplete?: string;
  placeholder?: string;
  /** Called with true once all requirements pass */
  onValidChange?: (valid: boolean) => void;
}

export function PasswordField({
  id = 'password',
  label = 'Senha',
  value,
  onChange,
  showChecklist = false,
  autoComplete = 'new-password',
  placeholder = '••••••••',
  onValidChange,
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);

  function handleChange(v: string) {
    onChange(v);
    if (onValidChange) {
      onValidChange(REQUIREMENTS.every(r => r.test(v)));
    }
  }

  const showList = showChecklist && value.length > 0;

  return (
    <div className="form-group" style={{ marginBottom: showList ? '0.5rem' : undefined }}>
      <label className="form-label" htmlFor={id}>{label}</label>

      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className="form-input"
          value={value}
          onChange={e => handleChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          style={{ paddingRight: '3rem' }}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
          style={{
            position: 'absolute',
            right: '0.875rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            padding: '0.25rem',
          }}
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {showList && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          {REQUIREMENTS.map(req => {
            const passes = req.test(value);
            return (
              <li
                key={req.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: 'var(--text-caption)',
                  color: passes ? '#86efac' : 'var(--text-muted)',
                  transition: 'color 200ms ease',
                }}
              >
                {passes
                  ? <Check size={13} style={{ color: '#4ade80', flexShrink: 0 }} />
                  : <X size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                {req.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Returns true if the value meets all password requirements. */
export function isPasswordValid(value: string): boolean {
  return REQUIREMENTS.every(r => r.test(value));
}
